import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fmt, api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';

const STATUS_COLORS = {
  good: '#00d4aa',
  warning: '#fbbf24',
  over: '#ef4444',
};

export default function CategoryBudgets({ defaultCollapsed, refreshKey, onRefresh, highlightCategory }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const rowRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const d = await api.getCategoryBudgets();
      setData(d);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // Refetch when anything changes elsewhere - new transactions shift the
  // 3-month suggestion basis even without touching this panel directly
  useEffect(() => { load(); }, [load, refreshKey]);

  const startEdit = (cat) => {
    setEditing(cat.category);
    setEditValue(Math.round(cat.budget));
  };

  const saveEdit = async () => {
    const amount = parseFloat(editValue);
    if (isNaN(amount) || amount < 0) return;
    setSaving(true);
    try {
      await api.setCategoryBudget(editing, amount);
      setEditing(null);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const resetToAuto = async (category) => {
    try {
      await api.resetCategoryBudget(category);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    }
  };

  // Scroll the flagged category into view once data's loaded, so a
  // "1 category is over budget" click from Needs Attention actually lands
  // on it instead of just the top of a collapsed page. Runs before the
  // !data early return below, since hooks must run unconditionally on
  // every render - this one is a no-op until data and the ref both exist.
  useEffect(() => {
    if (highlightCategory && rowRefs.current[highlightCategory]) {
      rowRefs.current[highlightCategory].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightCategory, data]);

  if (!data) return null;

  const hasAny = data.categories.length > 0;

  return (
    <CollapsiblePanel id="category-budgets" title="🗂 Category Budgets" defaultCollapsed={defaultCollapsed}>
      <p style={styles.helpText}>
        Each category gets an automatic monthly budget based on your last 3
        months of actual spending - most need no setup at all. Click any
        amount to override it manually; reset any time to go back to auto.
      </p>

      {hasAny && (
        <div style={styles.totalRow}>
          <span style={styles.totalLabel}>Total allocated across categories</span>
          <span style={styles.totalValue}>
            {fmt.currency(data.totalSpent)} <span style={styles.totalSlash}>/</span> {fmt.currency(data.totalBudgeted)}
          </span>
        </div>
      )}

      {!hasAny ? (
        <p style={styles.empty}>
          Once you have a few months of spending history, categories will
          show up here automatically.
        </p>
      ) : (
        <div style={styles.list}>
          {data.categories.map((cat) => {
            const color = STATUS_COLORS[cat.status];
            const barPct = cat.budget > 0 ? Math.min((cat.spent / cat.budget) * 100, 100) : 0;
            const isEditing = editing === cat.category;
            const isFlagged = highlightCategory === cat.category;

            return (
              <div
                key={cat.category}
                ref={(el) => { rowRefs.current[cat.category] = el; }}
                style={{ ...styles.row, ...(isFlagged ? styles.rowFlagged : {}) }}
              >
                {isFlagged && (
                  <p style={styles.flaggedBanner}>
                    ⚠️ DO THIS: {cat.category} is {fmt.currency(Math.abs(cat.remaining))} over its {fmt.currency(cat.budget)}/mo
                    budget - raise the budget below or review what's driving the extra spend in Transactions.
                  </p>
                )}
                <div style={styles.rowTop}>
                  <span style={styles.catName}>{cat.category}</span>
                  {!cat.isManual && <span style={styles.autoTag}>Auto</span>}
                  {isEditing ? (
                    <div style={styles.editRow}>
                      <span style={styles.dollarSign}>$</span>
                      <input
                        type="number"
                        min="0"
                        step="5"
                        style={styles.editInput}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                      />
                      <button style={styles.saveBtn} onClick={saveEdit} disabled={saving}>✓</button>
                      <button style={styles.cancelBtn} onClick={() => setEditing(null)}>✕</button>
                    </div>
                  ) : (
                    <button style={styles.amountBtn} onClick={() => startEdit(cat)}>
                      {fmt.currency(cat.spent)} <span style={styles.slash}>/</span> {fmt.currency(cat.budget)}
                    </button>
                  )}
                </div>

                <div style={styles.barTrack}>
                  <div style={{ ...styles.barFill, width: `${barPct}%`, background: color, boxShadow: `0 0 6px ${color}80` }} />
                </div>

                <div style={styles.rowBottom}>
                  <span style={{ color, fontWeight: '700' }}>
                    {cat.status === 'over'
                      ? `${fmt.currency(Math.abs(cat.remaining))} over`
                      : `${fmt.currency(cat.remaining)} left`}
                  </span>
                  {cat.isManual && (
                    <button style={styles.resetBtn} onClick={() => resetToAuto(cat.category)}>
                      Reset to auto
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </CollapsiblePanel>
  );
}

const styles = {
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.65rem 0.9rem',
    marginBottom: '1rem',
  },
  totalLabel: {
    color: '#9ca3af',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
  },
  totalValue: {
    color: '#e5e7eb',
    fontSize: '0.85rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  totalSlash: { color: '#4b5563' },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.7rem' },
  row: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.75rem 0.9rem',
  },
  rowFlagged: {
    border: '1px solid #ef4444',
    boxShadow: '0 0 0 3px rgba(239,68,68,0.15)',
  },
  flaggedBanner: {
    color: '#ef4444',
    fontSize: '0.74rem',
    fontWeight: '600',
    lineHeight: '1.5',
    margin: '0 0 0.6rem 0',
    fontFamily: "'Syne', sans-serif",
  },
  rowTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '6px',
  },
  catName: {
    color: '#e5e7eb',
    fontSize: '0.82rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
    flex: 1,
  },
  autoTag: {
    color: '#6b7280',
    fontSize: '0.6rem',
    fontWeight: '700',
    border: '1px solid #1e2a3a',
    borderRadius: '4px',
    padding: '1px 5px',
    fontFamily: "'Syne', sans-serif",
  },
  amountBtn: {
    background: 'transparent',
    border: 'none',
    color: '#9ca3af',
    fontSize: '0.78rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  slash: { color: '#4b5563' },
  editRow: { display: 'flex', alignItems: 'center', gap: '4px' },
  dollarSign: { color: '#6b7280', fontSize: '0.78rem' },
  editInput: {
    width: '70px',
    background: '#111827',
    border: '1px solid #00d4aa',
    borderRadius: '6px',
    padding: '3px 6px',
    color: '#fff',
    fontSize: '0.78rem',
    fontFamily: "'DM Mono', monospace",
  },
  saveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '5px',
    width: '22px',
    height: '22px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '700',
  },
  cancelBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '5px',
    width: '22px',
    height: '22px',
    cursor: 'pointer',
    fontSize: '0.7rem',
  },
  barTrack: {
    height: '5px',
    background: '#111827',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '6px',
  },
  barFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.6s ease',
  },
  rowBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  resetBtn: {
    background: 'transparent',
    border: 'none',
    color: '#4b5563',
    fontSize: '0.68rem',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
};
